'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, X, Smartphone, Wifi, WifiOff } from 'lucide-react';

export default function PWAProvider() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
      }
      // Also check for iOS Safari
      if ((window.navigator as any).standalone === true) {
        setIsInstalled(true);
      }
    };

    checkInstalled();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Check if user dismissed it previously
      if (localStorage.getItem('pwa_install_dismissed') === 'true') {
        return;
      }
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    };

    // Online/offline detection
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);

          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version available
                  showUpdateNotification();
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }

    // Request notification permission
    if ('Notification' in window && navigator.serviceWorker) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log('Notification permission granted');
        }
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const showUpdateNotification = () => {
    // Create a custom update notification
    const updateCard = document.createElement('div');
    updateCard.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
        max-width: 300px;
      ">
        <div style="
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          padding: 16px;
          border-left: 4px solid #3b82f6;
        ">
          <h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">Update Available</h4>
          <p style="margin: 0 0 12px 0; font-size: 14px; color: #666;">A new version of the app is available.</p>
          <div style="display: flex; gap: 8px;">
            <button onclick="window.location.reload()" style="
              background: #3b82f6;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 4px;
              font-size: 14px;
              cursor: pointer;
            ">Update Now</button>
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
              background: #f3f4f6;
              color: #374151;
              border: none;
              padding: 8px 16px;
              border-radius: 4px;
              font-size: 14px;
              cursor: pointer;
            ">Later</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(updateCard);
  };

  const handleDismiss = (forever: boolean = false) => {
    setShowInstallPrompt(false);
    if (forever) {
      localStorage.setItem('pwa_install_dismissed', 'true');
    }
  };

  // Don't render anything if app is installed or no prompt available
  if (isInstalled || !showInstallPrompt) {
    return null;
  }

  return (
    <>
      {/* Install Prompt */}
      {showInstallPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
          <Card className="border-l-4 border-l-blue-500 shadow-lg">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-500" />
                  <CardTitle className="text-lg">Install App</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismiss(false)}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                Install our app for a better experience with offline access and push notifications.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button onClick={handleInstallClick} className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Install
                  </Button>
                  <Button variant="outline" onClick={() => handleDismiss(false)}>
                    Later
                  </Button>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDismiss(true)}
                  className="text-xs text-muted-foreground h-auto py-1"
                >
                  Don't ask me again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Connection Status */}
      {!isOnline && (
        <div className="fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
          <Card className="border-l-4 border-l-orange-500 bg-orange-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium text-sm">You're offline</p>
                  <p className="text-xs text-muted-foreground">Some features may be limited</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Online Status Indicator */}
      {isOnline && (
        <div className="fixed top-4 right-4 z-40">
          <Badge variant="secondary" className="bg-green-100 text-green-800">
            <Wifi className="w-3 h-3 mr-1" />
            Online
          </Badge>
        </div>
      )}
    </>
  );
}
